'use client';

import { Box, Stack, Theme, Typography } from '@mui/material';
import tokens from '@verifime/design-tokens';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import CircularProgressButton from '../Form/CircularProgressButton';
import DisplayAlertErrors from '../Form/DisplayAlertErrors';
import { StickyActionBar } from '../StickyActionBar/StickyActionBar';

export type MagicLinkContinueProps = {
  /** Page heading. Customer-facing copy, so the caller owns it. */
  title: ReactNode;
  /** Rendered below the title when the inviting organisation is known.
   *  Supply a VerificationRequestedBy. */
  attribution?: ReactNode;
  /** Rendered below the attribution slot, visible only below the theme's `md` breakpoint: the
   *  same breakpoint `Landing` and `StickyActionBar` already switch this screen's other
   *  responsive treatments at, per the design board (the desktop split begins "at md and up").
   *  Supply an IdentityRequirementSummary built from the "documents" groups only: the
   *  camera/webcam requirement is desktop-only and never belongs in this slot. Pairs with
   *  `desktopRequirements` below; at any one width at most one of the two is visible. */
  requirements?: ReactNode;
  /** Rendered below `requirements`, visible only at and above the theme's `md` breakpoint: the
   *  desktop complement to `requirements`, opposed to it rather than layered alongside it, so the
   *  two can never both show (or both hide) at once. Supply an IdentityRequirementSummary built
   *  from the SAME "documents" groups `requirements` uses, plus a webcam requirement group,
   *  typically followed by a DesktopQrReveal. */
  desktopRequirements?: ReactNode;
  /** Rendered above the action. Supply an ExpiryClock. */
  expiry?: ReactNode;
  /** The action label. Pressing this is what consumes the link. */
  actionLabel: ReactNode;
  onContinue: () => void;
  isSubmitting?: boolean;
  /** Shown under the action when a redemption attempt failed. Caller owns it. */
  errorMessage?: ReactNode;
  'data-testid'?: string;
};

/**
 * Wraps an optional composed slot (attribution/requirements/expiry) so a slot that was supplied
 * but rendered nothing costs no space of its own. A slot prop being a non-null ReactNode only
 * proves an element was passed, not that it renders anything: ExpiryClock returns null for an
 * unparseable expiry, and IdentityRequirementSummary sets its own root to `display: none` below
 * its mobile breakpoint (see `allGroupsHiddenOnMobile`). Either way this wrapper's own content
 * collapses to zero height, but the outer Stack's `gap` still reserves a full gap on each side of
 * it: a zero-height flex item still counts as a gap participant, and only a `display: none` item
 * does not.
 *
 * The initial measurement runs synchronously in `useLayoutEffect`, before the browser paints, so
 * a slot that is empty from the start never flashes its dead gap first. ResizeObserver keeps
 * measuring afterwards, so a slot that starts non-empty and only later collapses (a viewport
 * resize crossing IdentityRequirementSummary's own breakpoint) is still pulled out of the gap.
 *
 * Known limitation, not fixed here: once this wrapper has hidden itself, a `display: none`
 * subtree generates no layout, so ResizeObserver cannot detect the content becoming non-empty
 * again (for example a phone rotated back to a width where IdentityRequirementSummary's groups
 * would show again). Neither real case above exercises that direction today. Solving it properly
 * needs an always-rendered, always-measured shadow copy of the slot, which is a heavier fix than
 * a gap-collapse guard warrants on its own; see TD-049 in docs/technical-debt.md.
 *
 * Skips measuring entirely when `ResizeObserver` does not exist, rather than falling back to a
 * one-off `getBoundingClientRect` read: jsdom (this package's own Vitest environment, and the one
 * PasswordlessLogin.test.tsx mounts this component under with no browser and no
 * Storybook/Playwright involved) implements neither. jsdom's `getBoundingClientRect` always
 * answers zero height for every element, since it runs no real layout engine, so measuring at all
 * there would read every slot as empty and hide it, not just skip refining an already-correct
 * "not empty" default.
 */
function CollapsibleSlot({ children }: Readonly<{ children: ReactNode }>): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    setIsEmpty(node.getBoundingClientRect().height === 0);

    const observer = new ResizeObserver(([entry]) => {
      setIsEmpty(entry.contentRect.height === 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ width: '100%', display: isEmpty ? 'none' : undefined }}>
      {children}
    </Box>
  );
}

/**
 * The magic-link landing screen that replaces "sign in the moment the page loads": loading this
 * component consumes nothing, and only pressing the action calls `onContinue`. A link that signs
 * the customer in on page load lets any URL fetch (a mail scanner, a link-preview bot) spend the
 * one-time ticket before the customer ever sees the page, locking the customer out each time; see
 * the investigation note "Magic link consumed by mail scanners - inert link decision" (VME
 * knowledge base / Obsidian vault, issue-investigation/) for the production evidence behind this.
 * This screen is the fix: the press is the entire mechanism.
 *
 * There is no email field: the artboard that used to show a read-only email block has dropped it,
 * and independently no unauthenticated endpoint in this system returns a customer's email address
 * - the magic link's own fragment carries a Cognito subject identifier, not an address, so this
 * screen could not obtain one before sign-in even if the design still wanted it. The screen must
 * still never grow an editable field of any kind: an input here, on any data, would let the screen
 * be used to discover whether an identifier has an account by watching how it responds.
 *
 * `attribution`, `requirements`, `desktopRequirements` and `expiry` are ReactNode slots (typically
 * a VerificationRequestedBy, an IdentityRequirementSummary, a second IdentityRequirementSummary
 * plus a DesktopQrReveal, and an ExpiryClock) rather than data props, so this component never
 * needs to know where a logo or a document rule came from (ADR-0050) and a caller can omit any of
 * them. `requirements` and `desktopRequirements` are opposed responsive slots, not layered ones:
 * see each prop's own doc comment below.
 *
 * Wrapped in withClarityTheme: the page renders this inside `Landing` (untouched by this stack, and
 * legacy per ADR-0078). This is this component's own theme boundary for its own directly-rendered
 * markup (the title, the action); it is not the only one in the composition.
 * `IdentityRequirementSummary`, `ExpiryClock` and `StatusIconTile` each wrap themselves too, and
 * `CompanyLogoAndName` (reached two levels down the `attribution` slot via
 * `VerificationRequestedBy`) already did. Nesting withClarityTheme is idempotent (it re-derives the
 * same Clarity theme from the ambient light/dark mode), so the overlap is harmless.
 */
function MagicLinkContinueComponent({
  title,
  attribution,
  requirements,
  desktopRequirements,
  expiry,
  actionLabel,
  onContinue,
  isSubmitting,
  errorMessage,
  'data-testid': dataTestId,
}: Readonly<MagicLinkContinueProps>): ReactElement {
  return (
    <Stack
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        width: '100%',
        boxSizing: 'border-box',
        alignItems: 'center',
        textAlign: 'center',
        gap: theme.semantic.spacing['2lg'],
        // Caps the card at a readable width on desktop and centres it there: the real page
        // (Landing) already centres its content column, but this component must not depend
        // on that. Without this, a parent that does not itself centre its children leaves
        // the card sitting hard against the left edge once maxWidth clips it below 100%.
        marginInline: 'auto',
        maxWidth: ['100%', theme.semantic.size.sm],
        padding: theme.semantic.spacing['2lg'],
      })}
    >
      <Typography
        variant="h4"
        sx={(theme: Theme) => ({ fontWeight: theme.semantic.fontWeight.semibold })}
      >
        {title}
      </Typography>

      {attribution && <CollapsibleSlot>{attribution}</CollapsibleSlot>}

      {expiry && <CollapsibleSlot>{expiry}</CollapsibleSlot>}

      {/* flexDirection: 'column': a `display: flex` Box defaults to `flex-direction: row`, and
          a ROW flex container never stretches a single child along its own main axis (that
          child sizes to its own content width instead, whatever the wrapper's own width). Both
          slots hold exactly one child, so `column` is the direction that actually needs
          flexing here; `align-items` keeps its own default (`stretch`), which is what stretches
          that child to the wrapper's full, already-correct `width: '100%'`. */}
      {requirements && (
        <Box sx={{ width: '100%', display: ['flex', 'flex', 'none'], flexDirection: 'column' }}>
          {requirements}
        </Box>
      )}

      {desktopRequirements && (
        <Box sx={{ width: '100%', display: ['none', 'none', 'flex'], flexDirection: 'column' }}>
          {desktopRequirements}
        </Box>
      )}

      {/* Button height: 48px via the theme's MuiButton root, unchanged. See docs/technical-debt.md TD-037. */}
      {/* contentInset="0px": this component's own Stack now carries its own 24px padding
          (`theme.semantic.spacing['2lg']`, board's value) on every side at xs. Leaving the
          bar's own default contentInset (also 24px) in place stacked a second, redundant 24px
          inset on top of the Stack's, measured directly against a real viewport at 48px total
          rather than the board's 24px. Passing 0px here matches how RecoverInvitation.tsx's own
          caller already handles the identical situation (its content supplies its own inset;
          see the comment in StickyActionBar.tsx). */}
      {/* parentPaddingBottom={tokens.spacing2lg}: negates this same 24px Stack padding on the
          bottom too, mirroring `parentPaddingX`'s own mechanism. Without it, the bar's own 28px
          bottom padding stacked on top of this Stack's 24px padding-bottom instead of being
          pulled down through it, leaving the bar 24px above the board's position. */}
      <StickyActionBar
        data-testid={dataTestId && `${dataTestId}-actionbar`}
        contentInset="0px"
        parentPaddingBottom={tokens.spacing2lg}
      >
        <CircularProgressButton
          isShowProgress={isSubmitting}
          onClick={onContinue}
          variant="contained"
          size="large"
          fullWidth
          data-action="continue-magic-link-sign-in"
        >
          {actionLabel}
        </CircularProgressButton>

        {errorMessage && (
          <Box sx={{ width: '100%' }}>
            <DisplayAlertErrors errors={[{ message: errorMessage }]} />
          </Box>
        )}
      </StickyActionBar>
    </Stack>
  );
}

export const MagicLinkContinue = withClarityTheme(MagicLinkContinueComponent);
